import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import TextAreaInput from "@/Components/UI/Forms/TextAreaInput";
import EmptyBoardActionItemsModal from "@/Components/UI/Modals/EmptyBoardActionItemsModal";
import {
    AiDraftContext,
    DraftState,
    initialDraftState,
} from "@/Contexts/AiDraftContext";
import { ExercisePickerContext } from "@/Contexts/ExercisePickerContext";
import { setGoalsBoardAttachedAtom } from "@/Pages/Chat/Partials/chatAtoms";
import { FollowupDraft } from "@/types/FollowupDraft";
import { CustomTask } from "@/types/followupTask";
import { Client, Coach, PageProps } from "@/types/page";
import { onUserEvent } from "@/utils/EventListener";
import {
    clearSavedMessage,
    getSavedMessage,
    scopedStorageKey,
    setSavedMessage,
} from "@/utils/savedMessage";
import Storage from "@/utils/Storage";
import { usePage } from "@/utils/turboSafeInertia";
import axios from "axios";
import { useSetAtom } from "jotai";
import {
    PropsWithChildren,
    useContext,
    useEffect,
    useRef,
    useState,
} from "react";
import { createPortal } from "react-dom";
import { IoClose } from "react-icons/io5";

type AiDraftPageProps = PageProps<{ otherUser?: Client | Coach }>;

type FollowupDraftCreatedBroadcastEvent = {
    followupDraft: FollowupDraft;
    coachId: Coach["id"];
    attachesGoalsBoard: boolean;
    isBoardUnmodified: boolean;
};
type FollowupDraftFailureBroadcastEvent = {
    coachId: Coach["id"];
    clientId: Client["id"];
};

export const AiDraftProvider = ({ children }: PropsWithChildren) => {
    const { user, otherUser } = usePage<AiDraftPageProps>().props;

    const { attachExercise, clearSelectedExercise } = useContext(
        ExercisePickerContext,
    );

    const setGoalsBoardAttached = useSetAtom(setGoalsBoardAttachedAtom);

    const [draftStates, setDraftStates] = useState<Record<number, DraftState>>(
        {},
    );
    const persistedDraftStates = useRef<Record<number, DraftState>>({});
    const [feedbackModalDraft, setFeedbackModalDraft] =
        useState<FollowupDraft>();

    const chatInputContainer = document.getElementById("chat-input-container");

    const draftStorageKey = (clientId: number, coachId: number) =>
        scopedStorageKey("coach", clientId, coachId, "aiDraft");

    const getDraftState = (otherUserId: number): DraftState => {
        const loadedDraftState = draftStates[otherUserId];
        if (loadedDraftState) {
            return loadedDraftState;
        }
        if (user?.type !== "coach") {
            return initialDraftState;
        }
        if (!persistedDraftStates.current[otherUserId]) {
            persistedDraftStates.current[otherUserId] = {
                ...initialDraftState,
                draft: Storage.load(
                    draftStorageKey(otherUserId, user.id),
                    null,
                ),
            };
        }
        return persistedDraftStates.current[otherUserId];
    };

    const updateDraftState = (
        otherUserId: number,
        changes: Partial<DraftState>,
    ) => {
        setDraftStates((previousDraftStates) => {
            const newDraftState = {
                ...(previousDraftStates[otherUserId] ??
                    getDraftState(otherUserId)),
                ...changes,
            };
            if ("draft" in changes && user?.type === "coach") {
                Storage.save(
                    draftStorageKey(otherUserId, user.id),
                    newDraftState.draft,
                );
            }
            return {
                ...previousDraftStates,
                [otherUserId]: newDraftState,
            };
        });
    };

    const getDraft = async (client: Client, customTasks?: CustomTask[]) => {
        if (user?.type !== "coach") {
            return;
        }
        const savedMessage = getSavedMessage("coach", client.id, user.id);
        if (savedMessage) {
            const confirmed = confirm(
                "Generating a follow up message will replace your message in progress. Are you sure you want to continue?",
            );
            if (!confirmed) {
                return;
            }
        }
        updateDraftState(client.id, { isProcessing: true });
        try {
            const response = await axios.post(
                route("followup-drafts.generate", client.id),
                {
                    customTasks: customTasks,
                },
            );
            if (response?.data?.draft) {
                updateDraftState(client.id, {
                    draft: response.data.draft,
                    isProcessing: false,
                });
                setSavedMessage(
                    "coach",
                    client.id,
                    user.id,
                    response.data.draft.text,
                );
                if (response.data.draft.exercise_id) {
                    attachExercise(client.id, response.data.draft.exercise_id);
                }
            }
        } catch (error) {
            console.error(error);
            updateDraftState(client.id, { isProcessing: false });
        }
    };

    const generateAllDrafts = async () => {
        if (user?.type !== "coach") {
            return;
        }
        const coachId = user.id;
        const response = await axios.post<{
            clientIds: number[];
            drafts: FollowupDraft[];
            generating: number[];
        }>(route("followup-drafts.generate-all"));
        response.data.clientIds.forEach((clientId) => {
            clearSavedMessage("coach", clientId, coachId);
            updateDraftState(clientId, { draft: null });
            clearSelectedExercise(clientId);
            setGoalsBoardAttached({ otherUserId: clientId, value: false });
        });
        response.data.drafts.forEach((draft) => {
            updateDraftState(draft.client_id, { draft, isProcessing: false });
            if (!getSavedMessage("coach", draft.client_id, coachId)) {
                setSavedMessage("coach", draft.client_id, coachId, draft.text);
            }
        });
        response.data.generating.forEach((clientId) => {
            updateDraftState(clientId, { isProcessing: true });
        });
    };

    const clearDraft = (otherUserId: number, probablyUsed: boolean) => {
        const { draft } = getDraftState(otherUserId);
        if (draft) {
            if (probablyUsed) {
                axios.patch(
                    route("followup-drafts.mark-as-probably-used", draft.id),
                );
            }
            axios.delete(route("followup-drafts.destroy", draft.id));
        }
        updateDraftState(otherUserId, { draft: null });
        clearSelectedExercise(otherUserId);
        setGoalsBoardAttached({ otherUserId, value: false });
    };

    const setStarsScore = (otherUserId: number, rating: number | null) => {
        const { draft } = getDraftState(otherUserId);
        if (!draft) return;
        axios.patch(route("followup-drafts.review", draft.id), {
            stars: rating,
        });
        updateDraftState(otherUserId, { draft: { ...draft, score: rating } });
    };

    const submitFeedback = () => {
        if (!feedbackModalDraft) return;
        axios.patch(route("followup-drafts.feedback", feedbackModalDraft.id), {
            feedback: feedbackModalDraft.feedback,
        });
        setFeedbackModalDraft(undefined);
        const { draft } = getDraftState(feedbackModalDraft.client_id);
        if (draft?.id === feedbackModalDraft.id) {
            updateDraftState(feedbackModalDraft.client_id, {
                draft: { ...draft, feedback: feedbackModalDraft.feedback },
            });
        }
    };

    useEffect(() => {
        persistedDraftStates.current = {};
        setDraftStates({});
    }, [user?.id, user?.type]);

    onUserEvent<FollowupDraftCreatedBroadcastEvent>(
        "FollowupDraftCreatedBroadcastEvent",
        (e) => {
            if (user?.type !== "coach") return;
            const clientId = e.followupDraft.client_id;
            if (e.attachesGoalsBoard) {
                setGoalsBoardAttached({ otherUserId: clientId, value: true });
            }
            updateDraftState(clientId, {
                draft: e.followupDraft,
                isProcessing: false,
                ...(e.isBoardUnmodified ? { isBoardUnmodified: true } : {}),
            });
            setSavedMessage("coach", clientId, user.id, e.followupDraft.text);
            if (e.followupDraft.exercise_id) {
                attachExercise(clientId, e.followupDraft.exercise_id);
            }
        },
    );

    onUserEvent<FollowupDraftFailureBroadcastEvent>(
        "FollowupDraftFailureBroadcastEvent",
        (e) => {
            updateDraftState(e.clientId, { isProcessing: false });
            if (e.clientId === otherUser?.id) {
                alert(
                    "An error occurred while generating the follow-up draft. Please try again later.",
                );
            }
        },
    );

    const focusedDraftState = otherUser
        ? getDraftState(otherUser.id)
        : initialDraftState;

    return (
        <AiDraftContext.Provider
            value={{
                getDraftState,
                getDraft,
                generateAllDrafts,
                clearDraft,
                setStarsScore,
                feedbackModalDraft,
                setFeedbackModalDraft,
            }}
        >
            {children}
            {feedbackModalDraft &&
                feedbackModalDraft.client_id === otherUser?.id &&
                chatInputContainer &&
                createPortal(
                    <div className="absolute -top-4 left-12 flex -translate-y-full flex-col gap-4 rounded-lg bg-brand-white shadow-xl drop-shadow-lg">
                        <div className="sticky top-0 h-0">
                            <IoClose
                                data-testid="close-icon"
                                className="absolute right-2 top-2 -mr-2 -mt-2 cursor-pointer rounded-bl-lg rounded-tr-lg p-3 text-5xl transition-colors hover:bg-gray-100"
                                onClick={() => setFeedbackModalDraft(undefined)}
                            />
                        </div>
                        <div className="ml-4 mr-12 mt-1 flex items-start justify-between">
                            <div className="text-brand-primary text-lg font-bold">
                                Thanks for the feedback, any details you would
                                like to add?
                            </div>
                        </div>
                        <div className="flex flex-col px-4 mb-1">
                            <TextAreaInput
                                className="flex w-full grow rounded-lg border border-brand-border p-2 hover:border-brand-red !h-24"
                                value={feedbackModalDraft.feedback ?? ""}
                                onChange={(e) =>
                                    setFeedbackModalDraft({
                                        ...feedbackModalDraft,
                                        feedback: e.target.value,
                                    })
                                }
                                // eslint-disable-next-line jsx-a11y/no-autofocus
                                autoFocus={true}
                                maxLength={1024}
                            />
                        </div>
                        <div className="mb-4 flex justify-center">
                            <PrimaryButton
                                onClick={() => submitFeedback()}
                                className="w-3/4 justify-center"
                                disabled={!feedbackModalDraft.feedback}
                            >
                                Submit
                            </PrimaryButton>
                        </div>
                    </div>,
                    chatInputContainer,
                )}
            {otherUser?.type === "client" &&
                focusedDraftState.isBoardUnmodified && (
                    <EmptyBoardActionItemsModal
                        id="empty-board-action-items-modal"
                        show={focusedDraftState.isBoardUnmodified}
                        disableBackgroundClickToClose={true}
                        client={otherUser}
                        onClose={() =>
                            updateDraftState(otherUser.id, {
                                isBoardUnmodified: false,
                            })
                        }
                    />
                )}
        </AiDraftContext.Provider>
    );
};
